import { useEffect } from "react";
import {
  Mic,
  MicOff,
  X,
  MessageSquare,
  Maximize2,
  Minimize2,
  Loader2,
  AlertTriangle,
  RotateCcw,
  Timer,
} from "lucide-react";
import VoiceOrb from "./VoiceOrb";
import ActionConfirmCard from "./ActionConfirmCard";
import VoiceCostMeter from "./VoiceCostMeter";
import { formatDuration } from "./voiceCost";

const STATUS_LABELS = {
  idle: "Preparazione…",
  connecting: "Connessione in corso…",
  listening: "In ascolto",
  thinking: "Sto pensando…",
  speaking: "Sto parlando",
  executing: "Sto lavorando…",
  awaiting_confirmation: "In attesa della tua conferma",
  ended: "Conversazione terminata",
  error: "EVA non è disponibile",
};

function statusLabel(status, muted) {
  if (muted && (status === "listening" || status === "awaiting_confirmation")) return "Microfono disattivato";
  return STATUS_LABELS[status] || "";
}

function ActivityDots() {
  return (
    <span className="inline-flex items-end gap-0.5 h-3" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="w-1 h-1 rounded-full bg-current animate-bounce motion-reduce:animate-none"
          style={{ animationDelay: `${i * 0.15}s` }}
        />
      ))}
    </span>
  );
}

function visibleActions(actions) {
  const open = actions.filter((a) => ["pending", "confirming", "cancelling"].includes(a.status));
  if (open.length) return open;
  const last = actions[actions.length - 1];
  return last ? [last] : [];
}

function RoundButton({ onClick, label, children, variant = "default", disabled, testId }) {
  const styles = {
    default: "bg-white/10 text-white hover:bg-white/20 border border-white/15",
    active: "bg-white text-slate-900 hover:bg-white/90",
    danger: "bg-rose-600 text-white hover:bg-rose-500",
  };
  return (
    <div className="flex flex-col items-center gap-1.5">
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={label}
        className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors disabled:opacity-40 ${styles[variant]}`}
        data-testid={testId}
      >
        {children}
      </button>
      <span className="text-[11px] text-white/60">{label}</span>
    </div>
  );
}

/** EVA, la chat vocale nel pannello laterale: orb centrale, niente trascrizioni, controlli in basso. */
export default function VoiceModeOverlay({
  voice,
  minimized,
  panelExpanded = true,
  onMinimize,
  onExpand,
  onClose,
  onClosePanel,
  onTogglePanel,
}) {
  const {
    status,
    error,
    clearError,
    muted,
    elapsed,
    maxSeconds,
    actions,
    sessionInfo,
    start,
    stop,
    cancelStart,
    toggleMute,
    confirmAction,
    cancelAction,
    getLevels,
    getLiveCost,
  } = voice;

  const isError = status === "error";
  const isConnecting = status === "connecting" || status === "idle";
  const remaining = maxSeconds ? Math.max(0, maxSeconds - elapsed) : null;
  const nearLimit = remaining !== null && remaining <= 60 && !isConnecting && !isError;
  const showCost = Boolean(sessionInfo?.showCost);
  const cards = visibleActions(actions);

  useEffect(() => {
    if (!error || isError) return undefined;
    const t = setTimeout(() => clearError(), 5000);
    return () => clearTimeout(t);
  }, [error, isError, clearError]);

  const endSession = () => {
    if (isConnecting) {
      cancelStart();
      onClose();
      return;
    }
    stop("user");
  };

  if (minimized && !isError) {
    return (
      <div className="space-y-2" data-testid="voice-mode-mini">
        {cards.map(({ action, status: actionStatus, message }) => (
          <ActionConfirmCard
            key={action.id}
            action={action}
            status={actionStatus}
            outcomeMessage={message}
            onConfirm={confirmAction}
            onCancel={cancelAction}
            tone="dark"
          />
        ))}
        <div className="flex items-center gap-3 rounded-full bg-slate-900/95 pl-2 pr-2 py-2 text-white shadow-2xl border border-white/10 backdrop-blur-md">
          <VoiceOrb status={status} getLevels={getLevels} size={36} />
          <div className="flex-1 min-w-0" aria-live="polite">
            <p className="text-sm font-semibold truncate">EVA</p>
            <p className={`text-[11px] tabular-nums truncate inline-flex items-center gap-1 ${nearLimit ? "text-amber-300" : "text-white/55"}`}>
              {statusLabel(status, muted)}
              {status === "executing" && <ActivityDots />}
              {" · "}
              {formatDuration(elapsed)}
              {nearLimit && ` · termina tra ${formatDuration(remaining)}`}
              {showCost && (
                <>
                  {" · "}
                  <VoiceCostMeter getLiveCost={getLiveCost} model={sessionInfo?.model} compact />
                </>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={toggleMute}
            className={`w-9 h-9 rounded-full flex items-center justify-center ${muted ? "bg-white text-slate-900" : "bg-white/10 hover:bg-white/20"}`}
            aria-label={muted ? "Riattiva microfono" : "Disattiva microfono"}
          >
            {muted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          </button>
          <button
            type="button"
            onClick={onExpand}
            className="w-9 h-9 rounded-full flex items-center justify-center bg-white/10 hover:bg-white/20"
            aria-label="Apri EVA"
          >
            <Maximize2 className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={endSession}
            className="w-9 h-9 rounded-full flex items-center justify-center bg-rose-600 hover:bg-rose-500"
            aria-label="Termina EVA"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col flex-1 min-h-0 h-full text-white bg-[radial-gradient(ellipse_at_top,_#0f3b3a_0%,_#020617_65%)]"
      role="region"
      aria-label="EVA, assistente vocale"
      data-testid="voice-mode-overlay"
    >
      <div className="px-4 pt-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-base font-semibold tracking-wide">EVA</p>
            <p className="text-[11px] text-white/50">Assistente vocale</p>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <div className={`flex items-center gap-1.5 text-sm tabular-nums mr-1 ${nearLimit ? "text-amber-300" : "text-white/70"}`}>
              <Timer className="w-4 h-4" />
              {formatDuration(elapsed)}
            </div>
            {onTogglePanel && (
              <button
                type="button"
                onClick={onTogglePanel}
                className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                aria-label={panelExpanded ? "Riduci pannello" : "Ingrandisci pannello"}
              >
                {panelExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </button>
            )}
            {onClosePanel && (
              <button
                type="button"
                onClick={onClosePanel}
                className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                aria-label="Chiudi assistente"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
        {showCost && !isError && (
          <VoiceCostMeter getLiveCost={getLiveCost} model={sessionInfo?.model} />
        )}
      </div>

      {nearLimit && (
        <div className="mx-auto mt-3 rounded-full bg-amber-400/15 border border-amber-300/30 px-3 py-1 text-xs text-amber-200">
          EVA termina tra {formatDuration(remaining)}
        </div>
      )}

      <div className="flex-1 flex flex-col items-center justify-center px-6 min-h-0">
        {isError ? (
          <div className="max-w-sm text-center space-y-4">
            <div className="mx-auto w-16 h-16 rounded-full bg-white/10 flex items-center justify-center">
              <AlertTriangle className="w-7 h-7 text-amber-300" />
            </div>
            <p className="text-lg font-semibold">{STATUS_LABELS.error}</p>
            <p className="text-sm text-white/70 leading-relaxed">{error}</p>
          </div>
        ) : (
          <>
            <VoiceOrb status={status} getLevels={getLevels} size={panelExpanded ? 180 : 120} />
            <div className="mt-8 text-center" aria-live="polite">
              <p className="text-base font-medium flex items-center justify-center gap-2">
                {isConnecting && <Loader2 className="w-4 h-4 animate-spin" />}
                {statusLabel(status, muted)}
                {status === "executing" && <ActivityDots />}
              </p>
              {status === "awaiting_confirmation" && !muted && (
                <p className="mt-1 text-xs text-white/55">Rispondi «sì» per confermare oppure usa i pulsanti qui sotto</p>
              )}
              {status === "listening" && !muted && elapsed < 8 && (
                <p className="mt-1 text-xs text-white/45">Parla pure: puoi interrompermi in qualsiasi momento</p>
              )}
            </div>
          </>
        )}
      </div>

      {!isError && cards.length > 0 && (
        <div className="px-5 pb-4 w-full max-w-md mx-auto space-y-2">
          {cards.map(({ action, status: actionStatus, message }) => (
            <ActionConfirmCard
              key={action.id}
              action={action}
              status={actionStatus}
              outcomeMessage={message}
              onConfirm={confirmAction}
              onCancel={cancelAction}
              tone="dark"
            />
          ))}
        </div>
      )}

      {error && !isError && (
        <div className="mx-auto mb-3 max-w-md rounded-xl bg-rose-500/15 border border-rose-400/30 px-3 py-2 text-xs text-rose-100">
          {error}
        </div>
      )}

      <div className="pb-10 pt-2 flex items-start justify-center gap-8">
        {isError ? (
          <>
            <RoundButton onClick={start} label="Riprova" testId="voice-retry">
              <RotateCcw className="w-6 h-6" />
            </RoundButton>
            <RoundButton onClick={onClose} label="Torna alla chat" testId="voice-back">
              <MessageSquare className="w-6 h-6" />
            </RoundButton>
          </>
        ) : (
          <>
            <RoundButton
              onClick={toggleMute}
              label={muted ? "Riattiva" : "Silenzia"}
              variant={muted ? "active" : "default"}
              disabled={isConnecting}
              testId="voice-mute"
            >
              {muted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
            </RoundButton>
            <RoundButton onClick={endSession} label="Termina" variant="danger" testId="voice-end">
              <X className="w-7 h-7" />
            </RoundButton>
            <RoundButton onClick={onMinimize} label="Torna alla chat" disabled={isConnecting} testId="voice-minimize">
              <MessageSquare className="w-6 h-6" />
            </RoundButton>
          </>
        )}
      </div>
    </div>
  );
}
